import { useState } from 'react';
import { ShieldAlert, ShieldCheck, ShieldQuestion, ShieldX } from 'lucide-react';
import type { BisScreenStatus } from '@/lib/api';
import { BisScreenModal } from './bis-screen-modal';

interface Props {
  customerPublicId: string;
  customerLegalName: string;
  customerCountryCode: string | null;
  /** RFQ id, threaded through so the modal can invalidate this RFQ's cache. */
  rfqPublicId?: string;

  bisScreenStatus: BisScreenStatus | null;
  bisLastScreenedAt: string | null;
  bisScreenNotes: string | null;
  bisScreenPdfKey: string | null;
  bisScreenPdfUrl: string | null;
  bisScreenDocumentFilename: string | null;
  bisScreenDocumentSha256: string | null;
}

const CHIP_CONF: Record<
  BisScreenStatus | 'not_screened',
  { label: string; cls: string; Icon: typeof ShieldCheck }
> = {
  clear:           { label: 'Screened: clear',   cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: ShieldCheck },
  flagged:         { label: 'Screened: flagged', cls: 'bg-red-100 text-red-800 border-red-200',             Icon: ShieldX },
  review_pending:  { label: 'Screen: review',    cls: 'bg-amber-100 text-amber-800 border-amber-200',       Icon: ShieldAlert },
  not_screened:    { label: 'Not screened',      cls: 'bg-muted text-muted-foreground border-border',       Icon: ShieldQuestion },
};

/**
 * Compact BIS/OFAC screen status chip + "Screen now" affordance.
 *
 * Rendered inline next to the customer name on the RFQ detail. Clicking
 * opens the BisScreenModal where the broker runs the trade.gov CSL search
 * and records the result. Status precedence (CHIP_CONF) doubles as a
 * legend for the colored states.
 */
export function CustomerScreenChip(props: Props) {
  const [open, setOpen] = useState(false);
  const conf = CHIP_CONF[props.bisScreenStatus ?? 'not_screened'];
  const Icon = conf.Icon;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={
          props.bisLastScreenedAt
            ? `Last screened ${new Date(props.bisLastScreenedAt).toLocaleDateString()}. Click to re-screen.`
            : 'No BIS/OFAC screen on file. Click to screen now.'
        }
        className={`inline-flex items-center gap-1 px-2.5 py-2 text-xs font-medium rounded border sm:px-1.5 sm:py-0.5 sm:text-[11px] ${conf.cls} hover:opacity-90`}
      >
        <Icon className="w-3 h-3" aria-hidden />
        {conf.label}
      </button>

      <BisScreenModal
        open={open}
        onClose={() => setOpen(false)}
        customerPublicId={props.customerPublicId}
        customerLegalName={props.customerLegalName}
        customerCountryCode={props.customerCountryCode}
        rfqPublicId={props.rfqPublicId}
        bisScreenStatus={props.bisScreenStatus}
        bisLastScreenedAt={props.bisLastScreenedAt}
        bisScreenNotes={props.bisScreenNotes}
        bisScreenPdfKey={props.bisScreenPdfKey}
        bisScreenPdfUrl={props.bisScreenPdfUrl}
        bisScreenDocumentFilename={props.bisScreenDocumentFilename}
        bisScreenDocumentSha256={props.bisScreenDocumentSha256}
      />
    </>
  );
}
